import { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import { API_ORIGIN } from '@/lib/config';

const AuthContext = createContext(null);

const BACKEND_URL = API_ORIGIN;
const API = `${BACKEND_URL}/api`;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchUser();
    } else {
      setLoading(false);
    }
  }, [token]);

  const fetchUser = async () => {
    try {
      const res = await axios.get(`${API}/auth/me`);
      setUser(res.data);
    } catch (e) {
      console.error('Auth error:', e);
      logout();
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    const res = await axios.post(`${API}/auth/login`, { email, password }, { withCredentials: true });
    const { token: newToken, refresh_token: newRefreshToken, user: userData } = res.data;
    localStorage.setItem('token', newToken);
    localStorage.setItem('refresh_token', newRefreshToken);
    axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
    setToken(newToken);
    setUser(userData);
    return userData;
  };

  const register = async (email, username, displayName, password, inviteCode, birthDate) => {
    const res = await axios.post(`${API}/auth/register`, {
      email,
      username,
      display_name: displayName,
      password,
      invite_code: inviteCode,
      // Required. The age check happens before an account exists, which is
      // the point of it - we should not hold a child's details at all.
      birth_date: birthDate
    }, { withCredentials: true });
    const { token: newToken, refresh_token: newRefreshToken, user: userData } = res.data;
    localStorage.setItem('token', newToken);
    localStorage.setItem('refresh_token', newRefreshToken);
    axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
    setToken(newToken);
    setUser(userData);
    return userData;
  };

  const refreshToken = async () => {
    try {
      const refreshTokenValue = localStorage.getItem('refresh_token');
      if (!refreshTokenValue) {
        throw new Error('No refresh token available');
      }

      const res = await axios.post(`${API}/auth/refresh`, { refresh_token: refreshTokenValue }, {
        timeout: 15000,
        withCredentials: true, // Send httpOnly cookie
        validateStatus: () => true // Don't throw on any status
      });

      // Backend refresh returns { access_token, refresh_token, token_type }
      const newToken = res.data?.access_token || res.data?.token;
      if (res.status === 200 && newToken) {
        localStorage.setItem('token', newToken);
        // Also rotate the refresh token if a new one was issued
        if (res.data.refresh_token) {
          localStorage.setItem('refresh_token', res.data.refresh_token);
        }
        axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
        setToken(newToken);
        return newToken;
      } else {
        throw new Error('Token refresh failed');
      }
    } catch (e) {
      console.error('Failed to refresh token:', e);
      logout();
      throw e;
    }
  };

  const logout = async () => {
    try {
      const refreshTokenValue = localStorage.getItem('refresh_token');
      if (refreshTokenValue) {
        // Attempt to notify backend of logout (best effort, don't fail if it doesn't work)
        try {
          await axios.post(`${API}/auth/logout`, { refresh_token: refreshTokenValue }, {
            timeout: 5000,
            withCredentials: true,
            validateStatus: () => true
          });
        } catch (e) {
          console.warn('Logout notification failed:', e);
        }
      }
    } finally {
      localStorage.removeItem('token');
      localStorage.removeItem('refresh_token');
      delete axios.defaults.headers.common['Authorization'];
      setToken(null);
      setUser(null);
    }
  };

  const refreshUser = async () => {
    try {
      const res = await axios.get(`${API}/auth/me`);
      setUser(res.data);
      return res.data;
    } catch (e) {
      console.error('Failed to refresh user:', e);
      throw e;
    }
  };

  const updateUser = (updates) => {
    setUser(prev => ({ ...prev, ...updates }));
  };

  return (
    <AuthContext.Provider value={{ user, token, loading, login, register, logout, refreshToken, updateUser, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
};
